import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import { authApi } from './lib/api/auth'
import type { Role } from './types'

import LandingPage from './pages/LandingPage'
import SignIn from './pages/auth/SignIn'
import SignUp from './pages/auth/SignUp'
import ForgotPassword from './pages/auth/ForgotPassword'
import SetPassword from './pages/auth/SetPassword'
import ResetPassword from './pages/auth/ResetPassword'
import InterviewerApply from './pages/InterviewerApply'
import ClientDashboard from './pages/client/ClientDashboard'
import InterviewerDashboard from './pages/interviewer/InterviewerDashboard'
import CalendlyCallback from './pages/interviewer/CalendlyCallback'
import AdminDashboard from './pages/admin/AdminDashboard'

// ── Protected Route ──────────────────────────────────────────────────────────
interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles: Role[]
}

function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, token } = useAuthStore()

  if (!token || !user) {
    return <Navigate to="/signin" replace />
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={`/${user.role}`} replace />
  }

  return <>{children}</>
}

// ── Public Route (redirect if authenticated) ─────────────────────────────────
interface PublicRouteProps {
  children: React.ReactNode
}

function PublicAuthRoute({ children }: PublicRouteProps) {
  const { user, logout } = useAuthStore()
  if (user) {
    if (!user.role) { logout(); return <>{children}</> }
    return <Navigate to={user.role === 'client' ? '/client/profile' : `/${user.role}`} replace />
  }
  return <>{children}</>
}

// ── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const { token, setUser, logout } = useAuthStore()
  const location = useLocation()

  // Validate token on mount and route changes
  useEffect(() => {
    if (!token) return

    authApi.me()
      .then((user) => setUser(user))
      .catch(() => logout())
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/apply-interviewer" element={<InterviewerApply />} />

      {/* Auth routes — redirect away if already logged in */}
      <Route path="/signin" element={<PublicAuthRoute><SignIn /></PublicAuthRoute>} />
      <Route path="/signup" element={<PublicAuthRoute><SignUp /></PublicAuthRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password" element={<SetPassword />} />

      {/* Calendly OAuth callback — matches CALENDLY_REDIRECT_URI path on the frontend domain */}
      <Route
        path="/api/calendly/oauth/callback"
        element={
          <ProtectedRoute allowedRoles={['interviewer']}>
            <CalendlyCallback />
          </ProtectedRoute>
        }
      />
      {/* Legacy path — kept for backward compatibility */}
      <Route
        path="/interviewer/calendly/callback"
        element={
          <ProtectedRoute allowedRoles={['interviewer']}>
            <CalendlyCallback />
          </ProtectedRoute>
        }
      />

      {/* Protected portals */}
      <Route
        path="/client/*"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviewer/*"
        element={
          <ProtectedRoute allowedRoles={['interviewer']}>
            <InterviewerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
